'use client';

import { IconDoubleChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-double-chevron-left-filled';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PaginationRootContext } from './context/pagination-root.context';
import type { PaginationJumpToFirstButtonPropsInterface } from './types/pagination-jump-to-first-button.types';

/**
 * The jump-to-first-page `button` element
 * @since 10.17.0
 */
export const PaginationJumpToFirstButton = forwardRef<
  HTMLButtonElement,
  PaginationJumpToFirstButtonPropsInterface
>((props: PaginationJumpToFirstButtonPropsInterface, forwardedRef) => {
  const {
    children = (
      <Icon>
        <IconDoubleChevronLeftFilled />
      </Icon>
    ),
    onClick,
    ...rest
  } = props;
  const { setPageIndex, currentPageIndex = 0 } = useContext(PaginationRootContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      setPageIndex?.(0);
      onClick?.(event);
    },
    [setPageIndex, onClick],
  );

  return (
    <IconButton
      data-c11n-component="Pagination.JumpToFirstButton"
      title="First page"
      tone="neutral"
      shape="circle"
      emphasis="bare"
      size="sm"
      onClick={handleClick}
      disabled={currentPageIndex <= 0}
      ref={forwardedRef}
      rel="first"
      {...rest}
      className={cx('c11n_pagination__jump-to-first-button', rest.className)}
    >
      {children}
    </IconButton>
  );
});

PaginationJumpToFirstButton.displayName = 'Pagination.JumpToFirstButton';
